/**
 * Codigo QR FALSO (RNF19). Nao codifica nada.
 *
 * Desenha um padrao deterministico a partir de um texto (o id da empresa) so
 * para a previa mostrar ONDE o QR da Evolution API vai aparecer. Um QR real
 * aqui enganaria: pareceria que da para escanear e parear um numero de
 * verdade. Os tres quadrados de canto sao os "finder patterns" que todo mundo
 * reconhece; o miolo vem de um gerador congruente linear (Park-Miller), sem
 * Math.random, para a mesma empresa mostrar sempre o mesmo desenho.
 *
 * Fundo branco e modulos pretos de proposito, nos dois temas: QR e um objeto
 * fisico reconhecivel, nao um cartao do painel.
 */

import { useMemo } from 'react';

const N = 25;
const FINDER = 7;

type Celula = readonly [number, number];

/** FNV-1a em 32 bits com `Math.imul` (multiplicacao exata), reduzido ao intervalo do Park-Miller. */
function hash(texto: string): number {
  let h = 2_166_136_261;
  for (let i = 0; i < texto.length; i += 1) {
    h = Math.imul(h ^ texto.charCodeAt(i), 16_777_619) >>> 0;
  }
  return (h % 2_147_483_646) + 1;
}

function dentroDeFinderOuMargem(x: number, y: number): boolean {
  const limite = FINDER + 1;
  const topoEsq = x < limite && y < limite;
  const topoDir = x >= N - limite && y < limite;
  const baseEsq = x < limite && y >= N - limite;
  return topoEsq || topoDir || baseEsq;
}

function finderPreto(x: number, y: number): boolean {
  const origens: readonly Celula[] = [
    [0, 0],
    [N - FINDER, 0],
    [0, N - FINDER],
  ];
  for (const [ox, oy] of origens) {
    const lx = x - ox;
    const ly = y - oy;
    if (lx < 0 || lx >= FINDER || ly < 0 || ly >= FINDER) {
      continue;
    }
    const anel = lx === 0 || lx === FINDER - 1 || ly === 0 || ly === FINDER - 1;
    const miolo = lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4;
    return anel || miolo;
  }
  return false;
}

function gerar(seed: string): readonly Celula[] {
  let estado = hash(seed);
  const proximo = (): number => {
    estado = (estado * 48_271) % 2_147_483_647;
    return estado;
  };
  const celulas: Celula[] = [];
  for (let y = 0; y < N; y += 1) {
    for (let x = 0; x < N; x += 1) {
      if (dentroDeFinderOuMargem(x, y)) {
        if (finderPreto(x, y)) {
          celulas.push([x, y]);
        }
        continue;
      }
      if (((proximo() >>> 7) & 1) === 1) {
        celulas.push([x, y]);
      }
    }
  }
  return celulas;
}

export function QrCodeFalso({ seed, size = 208 }: { readonly seed: string; readonly size?: number }): JSX.Element {
  const celulas = useMemo(() => gerar(seed), [seed]);
  return (
    <div className="inline-block rounded-xl bg-white p-3 shadow-soft">
      <svg
        viewBox={`0 0 ${N} ${N}`}
        width={size}
        height={size}
        role="img"
        aria-label="Código QR simulado; não pode ser escaneado"
        shapeRendering="crispEdges"
        className="block text-black"
      >
        {celulas.map(([x, y]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="currentColor" />
        ))}
      </svg>
    </div>
  );
}
